import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Navigation from '../components/Navigation';
import MatchBreakdown from '../components/MatchBreakdown';
import QuotaUpgradeModal from '../components/QuotaUpgradeModal';
import { useQuotaGate } from '../components/QuotaDisplay';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Heart, X, Bookmark, MapPin, Lock, Target, TrendingUp, Filter, Users, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to check if a profile has Investment Alignment/Strategy data filled
const hasInvestmentAlignmentData = (profile, role) => {
  if (role === 'investor') {
    // For investors, check Investment Preferences/Strategy fields
    const fields = ['preferred_round_structure', 'growth_preference', 'exit_horizon', 'ownership_preference', 'check_size_preference', 'co_investment_openness', 'investment_horizon', 'value_add_type'];
    return fields.some(f => {
      const val = profile[f];
      return val && ((Array.isArray(val) && val.length > 0) || (typeof val === 'string' && val.trim()));
    });
  } else if (role === 'founder') {
    // For founders, check Investment Alignment fields
    const fields = ['target_round_structure', 'growth_ambition', 'exit_vision', 'founder_ownership', 'primary_investor_type_sought'];
    return fields.some(f => {
      const val = profile[f];
      return val && ((Array.isArray(val) && val.length > 0) || (typeof val === 'string' && val.trim()));
    });
  }
  return false;
};

// Calculate Investment Alignment Compatibility between founder and investor preferences
const calculateAlignmentCompatibility = (founderProfile, investorProfile, viewerRole) => {
  if (!founderProfile || !investorProfile) return null;
  
  let matches = 0;
  let total = 0;
  const details = [];

  // For founder viewing investor: check if investor matches founder's preferences
  // For investor viewing founder: check if founder matches investor's preferences
  
  if (viewerRole === 'founder') {
    // Founder is viewing investor - check investor preferences against founder's alignment
    
    // Round Structure match
    if (founderProfile.target_round_structure && investorProfile.preferred_round_structure?.length > 0) {
      total++;
      if (investorProfile.preferred_round_structure.includes(founderProfile.target_round_structure)) {
        matches++;
        details.push({ field: 'Round Structure', match: true, value: founderProfile.target_round_structure });
      } else {
        details.push({ field: 'Round Structure', match: false, yours: founderProfile.target_round_structure, theirs: investorProfile.preferred_round_structure.join(', ') });
      }
    }

    // Growth preference match
    if (founderProfile.growth_ambition && investorProfile.growth_preference) {
      total++;
      if (founderProfile.growth_ambition === investorProfile.growth_preference) {
        matches++;
        details.push({ field: 'Growth Vision', match: true, value: founderProfile.growth_ambition });
      } else {
        details.push({ field: 'Growth Vision', match: false, yours: founderProfile.growth_ambition, theirs: investorProfile.growth_preference });
      }
    }

    // Exit horizon/vision match
    if (founderProfile.exit_vision && investorProfile.exit_horizon) {
      total++;
      // Map exit visions to approximate horizons
      const visionToHorizon = {
        'Strategic acquisition': ['3-5 years', '5-7 years'],
        'IPO': ['7-10 years', '10+ years'],
        'Private equity buyout': ['5-7 years', '7-10 years'],
        'Family business / long-term hold': ['10+ years', 'Flexible'],
        'Open to options': ['Flexible', '5-7 years', '7-10 years']
      };
      const compatibleHorizons = visionToHorizon[founderProfile.exit_vision] || [];
      if (compatibleHorizons.includes(investorProfile.exit_horizon) || investorProfile.exit_horizon === 'Flexible') {
        matches++;
        details.push({ field: 'Exit Timeline', match: true, value: `${founderProfile.exit_vision} / ${investorProfile.exit_horizon}` });
      } else {
        details.push({ field: 'Exit Timeline', match: false, yours: founderProfile.exit_vision, theirs: investorProfile.exit_horizon });
      }
    }

  } else if (viewerRole === 'investor') {
    // Investor is viewing founder - check founder's alignment against investor preferences
    
    // Round Structure match
    if (investorProfile.preferred_round_structure?.length > 0 && founderProfile.target_round_structure) {
      total++;
      if (investorProfile.preferred_round_structure.includes(founderProfile.target_round_structure)) {
        matches++;
        details.push({ field: 'Round Structure', match: true, value: founderProfile.target_round_structure });
      } else {
        details.push({ field: 'Round Structure', match: false, yours: investorProfile.preferred_round_structure.join(', '), theirs: founderProfile.target_round_structure });
      }
    }

    // Growth preference match
    if (investorProfile.growth_preference && founderProfile.growth_ambition) {
      total++;
      if (investorProfile.growth_preference === founderProfile.growth_ambition) {
        matches++;
        details.push({ field: 'Growth Vision', match: true, value: founderProfile.growth_ambition });
      } else {
        details.push({ field: 'Growth Vision', match: false, yours: investorProfile.growth_preference, theirs: founderProfile.growth_ambition });
      }
    }

    // Ownership preference match
    if (investorProfile.ownership_preference && founderProfile.founder_ownership) {
      total++;
      // Check compatibility between ownership ranges
      const ownershipCompatible = checkOwnershipCompatibility(founderProfile.founder_ownership, investorProfile.ownership_preference);
      if (ownershipCompatible) {
        matches++;
        details.push({ field: 'Ownership Fit', match: true, value: `Founder: ${founderProfile.founder_ownership}, Investor: ${investorProfile.ownership_preference}` });
      } else {
        details.push({ field: 'Ownership Fit', match: false, theirs: founderProfile.founder_ownership, yours: investorProfile.ownership_preference });
      }
    }
  }

  if (total === 0) return null;

  const score = Math.round((matches / total) * 100);
  return { score, matches, total, details };
};

// Helper to check ownership compatibility
const checkOwnershipCompatibility = (founderOwnership, investorOwnership) => {
  // Founder ownership ranges: '80–100%', '60–80%', '40–60%', '< 40%'
  // Investor ownership preferences: '< 5%', '5–10%', '10–20%', '20%+', 'Flexible'
  
  if (investorOwnership === 'Flexible') return true;
  
  const founderKeeps = {
    '80–100%': { min: 80, max: 100 },
    '60–80%': { min: 60, max: 80 },
    '40–60%': { min: 40, max: 60 },
    '< 40%': { min: 0, max: 40 }
  };
  
  const investorWants = {
    '< 5%': { min: 0, max: 5 },
    '5–10%': { min: 5, max: 10 },
    '10–20%': { min: 10, max: 20 },
    '20%+': { min: 20, max: 60 }
  };
  
  const fRange = founderKeeps[founderOwnership];
  const iRange = investorWants[investorOwnership];
  
  if (!fRange || !iRange) return false;
  
  // Check if investor's desired ownership fits with founder's remaining equity (100 - founder's min)
  const maxAvailable = 100 - fRange.min;
  return iRange.min <= maxAvailable;
};

// Threshold for "matching" profiles - profiles with score >= this are considered good matches
const MATCH_THRESHOLD = 50;

export default function Discovery() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [recommendations, setRecommendations] = useState([]);
  const [filteredRecs, setFilteredRecs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [myProfile, setMyProfile] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filterMode, setFilterMode] = useState('matching'); // 'matching' | 'all'
  
  // Quota gating for feature enforcement
  const { modalState, closeModal, consumeAndGate, loading: quotaLoading } = useQuotaGate();

  // Theme-aware classes - comprehensive
  const bg = isDark ? 'bg-slate-900' : 'bg-slate-100';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-blue-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-blue-700';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-600';
  const badgeBg = isDark ? 'bg-blue-900/60 text-blue-200 border border-blue-700/50' : 'bg-blue-100 text-blue-800';
  const badgeBgAlt = isDark ? 'bg-slate-600 text-slate-100 border border-slate-500' : 'bg-slate-200 text-slate-700';
  const inputBg = isDark ? 'bg-slate-800 border-slate-600 text-white placeholder:text-slate-400' : 'bg-white border-slate-200 text-blue-900';
  const btnSecondary = isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-100';
  const btnGhost = isDark ? 'text-slate-300 hover:text-white hover:bg-slate-700' : 'text-blue-700 hover:text-blue-800 hover:bg-slate-100';

  useEffect(() => {
    fetchRecommendations();
    fetchMyProfile();
    
    // Re-fetch when the page becomes visible again (e.g., user returns from editing profile)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchRecommendations();
        fetchMyProfile();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchMyProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/my-profile`);
      if (response.data.exists) {
        setMyProfile(response.data.profile);
      }
    } catch (error) {
      console.error('Error fetching my profile:', error);
    }
  };

  useEffect(() => {
    let filtered = recommendations;
    
    // Apply filter mode first (matching vs all)
    if (filterMode === 'matching') {
      filtered = recommendations.filter(rec => 
        rec.match_score >= MATCH_THRESHOLD || rec.is_matched
      );
    }
    
    // Then apply search term
    if (searchTerm) {
      filtered = filtered.filter(rec =>
        rec.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (rec.profile.startup_name && rec.profile.startup_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    setFilteredRecs(filtered);
  }, [searchTerm, recommendations, filterMode]);

  const fetchRecommendations = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    try {
      const response = await axios.get(`${API}/discovery/recommendations`);
      setRecommendations(response.data);
      if (showRefreshing) {
        toast.success('Recommendations refreshed');
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchRecommendations(true);
    fetchMyProfile();
  };

  const handleLike = async (targetUserId) => {
    // Determine quota type based on user role
    const quotaType = user?.role === 'founder' 
      ? 'match_suggestions' 
      : user?.role === 'investor' 
        ? 'browsing' 
        : 'match_suggestions';
    
    // Check and consume quota before liking
    const allowed = await consumeAndGate(quotaType, user?.role);
    if (!allowed) return; // Modal will be shown automatically
    
    try {
      const response = await axios.post(`${API}/discovery/like`, { target_user_id: targetUserId });
      if (response.data.matched) {
        toast.success('It\'s a match! 🎉');
      } else {
        toast.success('Liked!');
      }
      fetchRecommendations();
    } catch (error) {
      if (error.response?.status === 403) {
        // Feature entitlement error - show upgrade info
        toast.error('Upgrade required to like profiles');
      } else {
        toast.error('Failed to like');
      }
    }
  };

  const handlePass = async (targetUserId) => {
    try {
      await axios.post(`${API}/discovery/pass`, { target_user_id: targetUserId });
      toast.info('Passed');
      fetchRecommendations();
    } catch (error) {
      toast.error('Failed to pass');
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${bg}`}>
        <Navigation />
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className={`text-center ${textSecondary}`}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bg}`} data-testid="discovery-page">
      <Navigation />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-4xl font-bold mb-2 ${textPrimary}`}>
              {user?.role === 'founder' ? 'Discover Investors' : user?.role === 'investor' ? 'Discover Startups' : 'Discover Co-founders'}
            </h1>
            <p className={textSecondary}>Find your perfect match based on compatibility</p>
          </div>
          <Button
            onClick={handleRefresh}
            variant="outline"
            className={`border ${btnSecondary} gap-2`}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing...' : 'Refresh Matches'}
          </Button>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <Input
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`max-w-md ${inputBg}`}
            data-testid="search-input"
          />
          
          {/* Filter Toggle */}
          <div className={`flex items-center gap-2 p-1 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`} data-testid="filter-toggle">
            <button
              onClick={() => setFilterMode('matching')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                filterMode === 'matching'
                  ? 'bg-blue-600 text-white shadow-md'
                  : isDark 
                    ? 'text-slate-300 hover:bg-slate-700' 
                    : 'text-slate-600 hover:bg-slate-300'
              }`}
              data-testid="filter-matching"
            >
              <Target size={16} />
              <span>Best Matches</span>
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                filterMode === 'matching'
                  ? 'bg-blue-500 text-white'
                  : isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-300 text-slate-600'
              }`}>
                {recommendations.filter(r => r.match_score >= MATCH_THRESHOLD || r.is_matched).length}
              </span>
            </button>
            <button
              onClick={() => setFilterMode('all')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                filterMode === 'all'
                  ? 'bg-blue-600 text-white shadow-md'
                  : isDark 
                    ? 'text-slate-300 hover:bg-slate-700' 
                    : 'text-slate-600 hover:bg-slate-300'
              }`}
              data-testid="filter-all"
            >
              <Users size={16} />
              <span>View All</span>
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                filterMode === 'all'
                  ? 'bg-blue-500 text-white'
                  : isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-300 text-slate-600'
              }`}>
                {recommendations.length}
              </span>
            </button>
          </div>
        </div>

        {filteredRecs.length === 0 ? (
          <Card className={`shadow-sm p-12 text-center ${cardBg}`}>
            {filterMode === 'matching' && recommendations.length > 0 ? (
              <>
                <Target size={48} className={`mx-auto mb-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                <p className={`text-lg font-medium mb-2 ${textPrimary}`}>No strong matches found yet</p>
                <p className={`mb-4 ${textSecondary}`}>
                  Profiles with {MATCH_THRESHOLD}%+ compatibility will appear here.
                </p>
                <Button 
                  onClick={() => setFilterMode('all')}
                  className="btn-primary"
                  data-testid="show-all-btn"
                >
                  <Users size={16} className="mr-2" />
                  View All {recommendations.length} Profiles
                </Button>
              </>
            ) : searchTerm ? (
              <>
                <p className={`mb-2 ${textPrimary}`}>No results for "{searchTerm}"</p>
                <p className={textSecondary}>Try a different search term</p>
              </>
            ) : (
              <p className={textSecondary}>No recommendations found. Check back later!</p>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRecs.map(rec => (
              <Card key={rec.user_id} className={`shadow-sm p-6 ${cardBg}`} data-testid={`profile-card-${rec.user_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold mb-1 ${textPrimary}`}>
                      {user?.role === 'investor' && rec.profile.startup_name ? rec.profile.startup_name : rec.name}
                    </h3>
                    {user?.role === 'investor' && rec.profile.startup_name && (
                      <p className={`text-sm ${textSecondary}`}>{rec.name}</p>
                    )}
                  </div>
                  <span className="match-score-badge">{rec.match_score}%</span>
                </div>

                <div className="mb-4">
                  <div className={`flex items-center gap-2 text-sm mb-2 ${textSecondary}`}>
                    <MapPin size={14} />
                    <span>{rec.profile.city}, {rec.profile.country}</span>
                  </div>
                  
                  {user?.role === 'founder' && (
                    <div className="space-y-2">
                      <div>
                        <span className={`text-xs ${textMuted}`}>Type:</span>
                        <span className={`text-sm ml-2 ${textPrimary}`}>{rec.profile.investor_type}</span>
                      </div>
                      <div>
                        <span className={`text-xs ${textMuted}`}>Ticket Size:</span>
                        <span className={`text-sm ml-2 ${textPrimary}`}>
                          {rec.profile.currency} {rec.profile.ticket_size_min.toLocaleString()} - {rec.profile.ticket_size_max.toLocaleString()}
                        </span>
                      </div>
                      <div>
                        <span className={`text-xs ${textMuted}`}>Industries:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {rec.profile.preferred_industries.slice(0, 3).map(ind => (
                            <span key={ind} className={`text-xs px-2 py-1 rounded ${badgeBgAlt}`}>
                              {ind}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {user?.role === 'investor' && (
                    <div className="space-y-2">
                      <p className={`text-sm mb-2 line-clamp-2 ${textPrimary}`}>{rec.profile.one_line_pitch}</p>
                      <div>
                        <span className={`text-xs ${textMuted}`}>Stage:</span>
                        <span className={`text-sm ml-2 ${isDark ? 'text-blue-400' : 'text-blue-500'}`}>{rec.profile.stage}</span>
                      </div>
                      <div>
                        <span className={`text-xs ${textMuted}`}>Funding:</span>
                        <span className={`text-sm ml-2 ${textPrimary}`}>
                          {rec.profile.currency} {rec.profile.funding_asked_min.toLocaleString()} - {rec.profile.funding_asked_max.toLocaleString()}
                        </span>
                      </div>
                      <div>
                        <span className={`text-xs ${textMuted}`}>Industries:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {rec.profile.industries.slice(0, 3).map(ind => (
                            <span key={ind} className={`text-xs px-2 py-1 rounded ${badgeBg}`}>
                              {ind}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {user?.role === 'cofounder' && (
                    <div className="space-y-2">
                      <div>
                        <span className={`text-xs ${textMuted}`}>Skills:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {rec.profile.skills.slice(0, 3).map(skill => (
                            <span key={skill} className={`text-xs px-2 py-1 rounded ${badgeBg}`}>
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span className={`text-xs ${textMuted}`}>Looking for:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {rec.profile.looking_for.slice(0, 3).map(skill => (
                            <span key={skill} className={`text-xs px-2 py-1 rounded ${badgeBgAlt}`}>
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Match Score Breakdown - for founders and investors */}
                {(user?.role === 'founder' || user?.role === 'investor') && rec.match_breakdown && (
                  <MatchBreakdown 
                    breakdown={rec.match_breakdown} 
                    matchScore={rec.match_score}
                    isDark={isDark}
                  />
                )}

                {/* Investment Alignment Compatibility - shows how well preferences align */}
                {myProfile && (user?.role === 'founder' || user?.role === 'investor') && (() => {
                  // Determine the other profile's role
                  const otherRole = user?.role === 'founder' ? 'investor' : 'founder';
                  const otherProfile = rec.profile;
                  
                  // Check if the other profile has Investment Alignment data
                  const otherHasData = hasInvestmentAlignmentData(otherProfile, otherRole);
                  // Check if current user has Investment Alignment data
                  const myHasData = hasInvestmentAlignmentData(myProfile, user?.role);
                  
                  // Calculate alignment if both have data
                  const alignment = user?.role === 'founder' 
                    ? calculateAlignmentCompatibility(myProfile, rec.profile, 'founder')
                    : calculateAlignmentCompatibility(rec.profile, myProfile, 'investor');
                  
                  // If the other profile doesn't have alignment data, show CTA
                  if (!otherHasData) {
                    return (
                      <div className={`mt-3 p-3 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-center gap-2">
                          <Target size={14} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
                          <span className={`text-xs ${textMuted}`}>
                            {otherRole === 'investor' ? 'Investment Preferences' : 'Investment Alignment'} not yet shared
                          </span>
                        </div>
                        <p className={`text-xs mt-1 ${textMuted}`}>
                          Once {rec.name.split(' ')[0]} completes their profile, you'll see detailed compatibility insights here.
                        </p>
                      </div>
                    );
                  }
                  
                  // If current user doesn't have alignment data, show CTA to complete their profile
                  if (!myHasData && otherHasData) {
                    return (
                      <div className={`mt-3 p-3 rounded-lg border ${isDark ? 'bg-blue-900/20 border-blue-700/50' : 'bg-blue-50 border-blue-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Target size={14} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
                          <span className={`text-xs font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                            Unlock Better Matching
                          </span>
                        </div>
                        <p className={`text-xs ${textMuted}`}>
                          Complete your {user?.role === 'founder' ? 'Investment Alignment' : 'Investment Preferences'} section to see how well you align with this {otherRole}.
                        </p>
                        <Link to="/profile/setup" className="inline-block mt-2">
                          <span className={`text-xs font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} underline`}>
                            Complete your profile →
                          </span>
                        </Link>
                      </div>
                    );
                  }
                  
                  // If alignment couldn't be calculated (no common criteria), show info
                  if (!alignment) {
                    return (
                      <div className={`mt-3 p-3 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-center gap-2">
                          <Target size={14} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
                          <span className={`text-xs ${textMuted}`}>
                            No overlapping criteria to compare
                          </span>
                        </div>
                      </div>
                    );
                  }
                  
                  const scoreColor = alignment.score >= 70 
                    ? (isDark ? 'text-green-400' : 'text-green-600')
                    : alignment.score >= 40 
                      ? (isDark ? 'text-yellow-400' : 'text-yellow-600')
                      : (isDark ? 'text-orange-400' : 'text-orange-600');
                  
                  const scoreBg = alignment.score >= 70 
                    ? (isDark ? 'bg-green-900/30 border-green-700/50' : 'bg-green-50 border-green-200')
                    : alignment.score >= 40 
                      ? (isDark ? 'bg-yellow-900/30 border-yellow-700/50' : 'bg-yellow-50 border-yellow-200')
                      : (isDark ? 'bg-orange-900/30 border-orange-700/50' : 'bg-orange-50 border-orange-200');
                  
                  return (
                    <div className={`mt-3 p-3 rounded-lg border ${scoreBg}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Target size={14} className={scoreColor} />
                        <span className={`text-xs font-semibold ${scoreColor}`}>
                          Investment Alignment: {alignment.score}%
                        </span>
                        <span className={`text-xs ${textMuted}`}>
                          ({alignment.matches}/{alignment.total} criteria)
                        </span>
                      </div>
                      <div className="space-y-1">
                        {alignment.details.slice(0, 3).map((detail, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            <span className={detail.match ? (isDark ? 'text-green-400' : 'text-green-600') : (isDark ? 'text-orange-400' : 'text-orange-500')}>
                              {detail.match ? '✓' : '○'}
                            </span>
                            <span className={textMuted}>{detail.field}:</span>
                            {detail.match ? (
                              <span className={isDark ? 'text-green-300' : 'text-green-700'}>{detail.value}</span>
                            ) : (
                              <span className={isDark ? 'text-orange-300' : 'text-orange-600'}>
                                {user?.role === 'founder' ? `You: ${detail.yours || 'N/A'} vs ${detail.theirs || 'N/A'}` : `${detail.theirs || 'N/A'} vs Your: ${detail.yours || 'N/A'}`}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                <div className="flex gap-2 mt-4">
                  <Link to={`/profile/${rec.user_id}`} className="flex-1">
                    <Button className={`w-full border ${btnSecondary}`} variant="outline" data-testid={`view-profile-${rec.user_id}`}>
                      View Profile
                    </Button>
                  </Link>
                  {!rec.is_matched && !rec.already_liked && (
                    <>
                      <div className="relative group">
                        <Button
                          onClick={() => handleLike(rec.user_id)}
                          className="btn-primary"
                          size="icon"
                          data-testid={`like-${rec.user_id}`}
                        >
                          <Heart size={18} />
                        </Button>
                        {/* Tooltip for matching clarity */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                          <span className="font-semibold">Express Interest</span>
                          <p className="text-slate-300 mt-0.5">If they like you back, you'll match!</p>
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                        </div>
                      </div>
                      <Button
                        onClick={() => handlePass(rec.user_id)}
                        variant="ghost"
                        size="icon"
                        className={btnGhost}
                        data-testid={`pass-${rec.user_id}`}
                        title="Pass - Not interested"
                      >
                        <X size={18} />
                      </Button>
                    </>
                  )}
                  {rec.is_matched && (
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <Heart size={16} fill="currentColor" />
                      <span>Matched</span>
                    </div>
                  )}
                  {rec.already_liked && !rec.is_matched && (
                    <div className={`text-sm ${isDark ? 'text-blue-400' : 'text-blue-500'}`}>Liked</div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Quota Upgrade Modal */}
      <QuotaUpgradeModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        quotaType={modalState.quotaType}
        used={modalState.used}
        limit={modalState.limit}
        planName={modalState.planName}
        role={modalState.role || user?.role}
      />
    </div>
  );
}
